import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { User } from '../types';

interface AuthState {
  accessToken: string | null;
  user: User | null;
  isAuthenticated: boolean;
  setAuth: (token: string, user: User) => void;
  logout: () => void;
}

// Token storage strategy:
//
// DEMO (this implementation):
//   Access token stored in sessionStorage — survives tab refresh but not browser close.
//
// PRODUCTION recommendation:
//   - Access token: keep only in Zustand memory (never persisted to storage)
//   - Refresh token: httpOnly cookie set by the server — inaccessible to JS, immune to XSS
//   - On app load: call GET /auth/refresh; the browser sends the cookie automatically,
//     and the server returns a new short-lived access token that lives only in memory
export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      accessToken: null,
      user: null,
      isAuthenticated: false,
      setAuth: (accessToken, user) => set({ accessToken, user, isAuthenticated: true }),
      logout: () => set({ accessToken: null, user: null, isAuthenticated: false }),
    }),
    {
      name: 'auth',
      storage: createJSONStorage(() => sessionStorage),
    },
  ),
);
